/**
 * Family Management Component
 * Admin view for family accounts: list families, inspect a family (guardians,
 * students, linked devices with revoke), create a family with its first
 * guardian, and edit family name/active flag. Reuses the same backend
 * endpoints as the guardian self-service area, with explicit familyAccountId.
 */

import { EmptyState } from '../../components/empty-state';
import { Modal } from '../../components/modal';
import { Table } from '../../components/table';
import { FormField, Input, FormMessage } from '../../components/form-field';
import { saveFailure, loadFailure, requestFailure, type FormFailure } from './save-failure-words';
import { activeTone } from './status-tone';
import { Pill } from '../../components/pill';
import { useState } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../shell/common-header';
import {
  useAdminFamilies,
  useAdminFamilyDetail,
  useAdminFamilyDevices,
  useAdminRevokeFamilyDevice,
  useAdminCreateFamily,
  useAdminUpdateFamily,
} from '../../api/admin/families/hooks';
import type { AdminCreateFamilyPayload } from '../../api/admin/families/types';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import admin from './admin-dashboard.module.css';

interface FamilyManagementProps {
  onBack: () => void;
}

export function FamilyManagement({ onBack }: FamilyManagementProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const familiesQuery = useAdminFamilies(page, 20);
  const families = familiesQuery.data?.data ?? [];
  const totalPages = familiesQuery.data?.totalPages ?? 1;
  const error = familiesQuery.isError ? loadFailure(t, familiesQuery.error, 'families') : null;

  if (selectedId) {
    return (
      <FamilyDetail
        familyId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div className="portal-shell">
      <CommonHeader title={t('admin.families.title')} showBackButton onBackClick={onBack} />

      <main className={admin.adminPanelMain}>
        <div className={admin.adminTableWrapper}>
          <div className={admin.adminTableHeader}>
            <h2 className={admin.adminTableTitle}>{t('admin.families.listTitle')}</h2>
            <Button type="button" onClick={() => setShowCreateForm(true)}
            >
              {t('admin.families.create')}
            </Button>
          </div>

          {familiesQuery.isLoading && <div className={admin.adminLoading}>{t('common.loading')}</div>}

          {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

          {!familiesQuery.isLoading && !error && families.length === 0 && (
            <EmptyState icon="👨‍👩‍👧‍👦">{t('admin.families.empty')}</EmptyState>
          )}

          {families.length > 0 && (
            <Table flush>
              <thead>
                <tr>
                  <th>{t('admin.families.columns.guardians')}</th>
                  <th>{t('admin.families.columns.guardianCount')}</th>
                  <th>{t('admin.families.columns.students')}</th>
                  <th>{t('admin.families.columns.devices')}</th>
                  <th>{t('admin.families.columns.status')}</th>
                  <th>{t('admin.families.columns.created')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {families.map((family) => (
                  <tr key={family.id}>
                    <td>{family.guardianNames.join(', ') || '—'}</td>
                    <td>{family.guardianCount}</td>
                    <td>{family.studentCount}</td>
                    <td>{family.deviceCount}</td>
                    <td>
                      <Pill tone={activeTone(family.isActive)}>
                        {family.isActive ? t('admin.families.active') : t('admin.families.inactive')}
                      </Pill>
                    </td>
                    <td>{family.createdAt.slice(0, 10)}</td>
                    <td>
                      <div className={admin.adminRowActions}>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedId(family.id)}
                        >
                          {t('admin.families.view')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className={admin.adminPagination}>
              <div className={admin.adminPaginationButtons}>
                <Button type="button" variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}
                >
                  ←
                </Button>
                <span>{page} / {totalPages}</span>
                <Button type="button" variant="ghost" size="sm" disabled={page>= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  →
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {showCreateForm && (
        <CreateFamilyModal
          onClose={() => setShowCreateForm(false)}
          onCreated={() => setShowCreateForm(false)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Family detail (guardians, students, devices)                        */
/* ------------------------------------------------------------------ */

function FamilyDetail({ familyId, onBack }: { familyId: string; onBack: () => void }) {
  const { t } = useTranslation();
  const detailQuery = useAdminFamilyDetail(familyId);
  const updateFamily = useAdminUpdateFamily();
  const revokeDevice = useAdminRevokeFamilyDevice();
  const family = detailQuery.data;

  const [actionError, setError] = useState<FormFailure | null>(null);
  const error = actionError ?? (detailQuery.isError ? loadFailure(t, detailQuery.error, 'family') : null);

  const handleToggleActive = async () => {
    if (!family) return;
    setError(null);
    try {
      await updateFamily.mutateAsync({ id: familyId, payload: { isActive: !family.isActive } });
    } catch (e) {
      setError(saveFailure(t, e, 'family'));
    }
  };

  const handleRevokeDevice = async (deviceId: string, name?: string) => {
    if (!(await confirmDialog({ message: t('guardian.devices.confirmRevoke', { name: name ?? deviceId }), tone: 'danger' }))) return;
    setError(null);
    try {
      await revokeDevice.mutateAsync({ deviceId, familyId });
    } catch (e) {
      setError(requestFailure(t, e, 'revoke'));
    }
  };

  return (
    <div className="portal-shell">
      <CommonHeader
        title={
          family?.guardians.map((g) => g.name).join(', ') || t('admin.families.detailTitle')
        }
        showBackButton
        onBackClick={onBack}
      />

      <main className={admin.adminPanelMain}>
        {detailQuery.isLoading && <div className={admin.adminLoading}>{t('common.loading')}</div>}
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

        {family && (
          <>
            <div className={admin.adminTableWrapper}>
              <div className={admin.adminTableHeader}>
                <h2 className={admin.adminTableTitle}>{t('admin.families.detailTitle')}</h2>
                <Button type="button" variant="ghost" size="sm" onClick={() => void handleToggleActive()}
                  disabled={updateFamily.isPending}
                >
                  {family.isActive ? t('admin.families.deactivate') : t('admin.families.activate')}
                </Button>
              </div>

              <h3 className={admin.adminTableTitle}>{t('admin.families.guardiansTitle')}</h3>
              <Table flush>
                <thead>
                  <tr>
                    <th>{t('admin.families.columns.name')}</th>
                    <th>{t('login.email')}</th>
                    <th>{t('signup.phone')}</th>
                  </tr>
                </thead>
                <tbody>
                  {family.guardians.map((g) => (
                    <tr key={g.userId}>
                      <td>{g.name}</td>
                      <td>{g.email ?? '—'}</td>
                      <td>{g.phoneNumber ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h3 className={admin.adminTableTitle}>{t('admin.families.studentsTitle')}</h3>
              {family.students.length === 0 ? (
                <EmptyState>{t('guardian.students.empty')}</EmptyState>
              ) : (
                <Table flush>
                  <thead>
                    <tr>
                      <th>{t('admin.families.columns.name')}</th>
                      <th>{t('signup.birthDate')}</th>
                      <th>{t('admin.families.columns.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {family.students.map((s) => (
                      <tr key={s.userId}>
                        <td>{s.name}</td>
                        <td>{s.birthDate.slice(0, 10)}</td>
                        <td>
                          <Pill tone={activeTone(s.isActive)}>
                            {s.isActive ? t('admin.families.active') : t('admin.families.inactive')}
                          </Pill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h3 className={admin.adminTableTitle}>{t('admin.families.devicesTitle')}</h3>
              <FamilyDevicesTable familyId={familyId} onRevoke={handleRevokeDevice} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/** Devices sub-table (fetched via the shared family endpoints with explicit id) */
function FamilyDevicesTable({
  familyId,
  onRevoke,
}: {
  familyId: string;
  onRevoke: (deviceId: string, name?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const query = useAdminFamilyDevices(familyId);
  if (query.isLoading) return <div className={admin.adminLoading}>{t('common.loading')}</div>;
  if (query.isError) {
    const error = loadFailure(t, query.error, 'devices');
    return <FormMessage title={error.title}>{error.detail}</FormMessage>;
  }
  const devices = query.data ?? [];

  const active = devices.filter((d) => !d.revoked);
  if (active.length === 0) {
    return (
      <EmptyState>{t('guardian.devices.empty')}</EmptyState>
    );
  }

  return (
    <Table flush>
      <thead>
        <tr>
          <th>{t('admin.families.columns.name')}</th>
          <th>{t('guardian.devices.lastUsed')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {active.map((device) => (
          <tr key={device.id}>
            <td>{device.name}</td>
            <td>{device.lastUsedAt.slice(0, 10)}</td>
            <td>
              <div className={admin.adminRowActions}>
                <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void onRevoke(device.id, device.name)}
                >
                  {t('guardian.devices.revoke')}
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/* ------------------------------------------------------------------ */
/* Create family modal                                                 */
/* ------------------------------------------------------------------ */

function CreateFamilyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const createFamily = useAdminCreateFamily();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });
  const [error, setError] = useState<FormFailure | null>(null);

  const setField = (field: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Guardians are passwordless: they sign in via emailed magic links
    const payload: AdminCreateFamilyPayload = {
      guardian: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber || undefined,
      },
    };
    try {
      await createFamily.mutateAsync(payload);
      onCreated();
    } catch (err) {
      setError(saveFailure(t, err, 'family'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={t('admin.families.createTitle')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <div className={admin.adminFormRow}>
          <FormField label={t('signup.firstName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t('signup.lastName')} required>
            {(field) => (
              <Input {...field}
                type="text"
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                required
              />
            )}
          </FormField>
        </div>
        <FormField label={t('login.email')} required>
          {(field) => (
            <Input {...field}
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              required
            />
          )}
        </FormField>
        <FormField label={t('signup.phone')}>
          {(field) => (
            <Input {...field}
              type="tel"
              value={form.phoneNumber}
              onChange={(e) => setField('phoneNumber', e.target.value)}
            />
          )}
        </FormField>
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}
        <div className={admin.adminFormActions}>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={createFamily.isPending}>
            {createFamily.isPending ? t('common.loading') : t('admin.families.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
